import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminRouteV2ControllerGetRoutes } from "service";
import { RouteWithSkippedOrders, SkippedOrderLocation } from "service";

export interface SkippedOrderRegionGroup {
  regionId: string;
  regionName: string;
  regionColor?: string;
  locations: SkippedOrderLocation[];
}

export interface UseSkippedOrders {
  regionGroups: SkippedOrderRegionGroup[];
  totalCount: number;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Skipped orders for the given route-planner date, grouped by region. The
 * backend already filters each route's `locations` down to just the
 * requested orderStatuses when that param is passed (see
 * RouteV2Service.enrichRouteWithOrders), so every location returned here is
 * already "skipped" - no client-side status filtering needed. Region
 * grouping itself has to happen client-side, though: a route belongs to one
 * region, but the routes endpoint has no "group by region" of its own.
 */
const useSkippedOrders = (date: string): UseSkippedOrders => {
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useAdminRouteV2ControllerGetRoutes({
    queryParams: {
      date,
      includeLocations: true,
      orderStatuses: ["skipped"],
    },
  });

  // This hook's query key (orderStatuses=["skipped"]) is separate from the
  // main route-planner page's own routes query, so when the page marks an
  // order as skipped/regular (map "Move to Skipped", etc.) its own
  // `refetchAll()` never touches this one - it has no idea this hook exists.
  // With the default 2 min staleTime (see query-client.ts) that leaves the
  // badge/modal showing whatever they saw on mount until something forces a
  // refetch. Watch the global mutation cache instead: any mutation that
  // completes for this exact date's routes (customer-orders, driver-status,
  // transfer, etc. all key off pathParams.date) should refresh this list too.
  useEffect(() => {
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (
        event?.type !== "updated" ||
        event.mutation.state.status !== "success"
      ) {
        return;
      }
      const variables = event.mutation.state.variables as
        | { pathParams?: { date?: string } }
        | undefined;
      if (variables?.pathParams?.date === date) {
        refetch();
      }
    });
    return unsubscribe;
  }, [queryClient, date, refetch]);

  const regionGroups = useMemo(() => {
    const routes = (data?.data ?? []) as RouteWithSkippedOrders[];
    const groupsByRegionId = new Map<string, SkippedOrderRegionGroup>();

    routes.forEach((route) => {
      const skippedLocations = route.locations ?? [];
      if (skippedLocations.length === 0) return;

      const regionId = route.region?.id ?? route.regionId ?? "unassigned";
      const existing = groupsByRegionId.get(regionId);

      if (existing) {
        existing.locations.push(...skippedLocations);
        return;
      }

      groupsByRegionId.set(regionId, {
        regionId,
        regionName: route.region?.name ?? "Unassigned",
        regionColor: route.region?.color,
        locations: [...skippedLocations],
      });
    });

    return Array.from(groupsByRegionId.values()).sort((a, b) =>
      a.regionName.localeCompare(b.regionName),
    );
  }, [data]);

  const totalCount = useMemo(
    () => regionGroups.reduce((sum, group) => sum + group.locations.length, 0),
    [regionGroups],
  );

  return { regionGroups, totalCount, isLoading, refetch };
};

export default useSkippedOrders;
